import { useCallback, useEffect, useState } from 'react';
import { format, subDays } from 'date-fns';
import { billingApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { DateRangeFields } from '@/components/ui/DateRangeFields';
import { Modal } from '@/components/ui/Modal';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useDebounce } from '@/hooks/useDebounce';
import { usePagination } from '@/hooks/usePagination';
import type { InvoiceResponse } from '@/types/billing';
import type { SalesReturnItemRequest, SalesReturnRequest } from '@/types/billing';
import { formatCurrency, formatDateTime } from '@/utils/format';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

export function InvoicesPage() {
  const store = useAppSelector((s) => s.app.selectedStore);
  const currency = store?.currencyCode ?? 'INR';
  const pagination = usePagination();
  const debouncedSearch = useDebounce(pagination.search);
  const [from, setFrom] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [to, setTo] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [data, setData] = useState<InvoiceResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<InvoiceResponse | null>(null);
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnQty, setReturnQty] = useState<Record<number, string>>({});
  const [returnReason, setReturnReason] = useState('');
  const [refundPayment, setRefundPayment] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [printing, setPrinting] = useState(false);

  const load = useCallback(async () => {
    if (!store?.id) return;
    setLoading(true);
    try {
      const res = await billingApi.listInvoices({
        page: pagination.page,
        size: pagination.size,
        search: debouncedSearch || undefined,
        storeId: store.id,
        from: `${from}T00:00:00`,
        to: `${to}T23:59:59`,
      });
      setData(res.content);
      pagination.applyPageResponse(res);
    } catch (err) {
      showApiError(err, 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [store?.id, pagination.page, pagination.size, debouncedSearch, pagination, from, to]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = async (id: number) => {
    try {
      const inv = await billingApi.getInvoice(id);
      setDetail(inv);
      const qty: Record<number, string> = {};
      inv.items?.forEach((item) => {
        qty[item.productId] = '0';
      });
      setReturnQty(qty);
      setReturnReason('');
      setRefundPayment(true);
    } catch (err) {
      showApiError(err, 'Failed to load invoice');
    }
  };

  const processReturn = async () => {
    if (!detail) return;
    const items: SalesReturnItemRequest[] = [];
    for (const item of detail.items ?? []) {
      const qty = Number(returnQty[item.productId] ?? 0);
      if (qty <= 0) continue;
      if (qty > item.quantity) {
        notify.error(`Return qty exceeds sold for ${item.productName}`);
        return;
      }
      items.push({ productId: item.productId, quantity: qty });
    }

    if (items.length === 0) {
      notify.error('Enter return quantities for at least one line');
      return;
    }

    const body: SalesReturnRequest = {
      invoiceId: detail.id,
      reason: returnReason || undefined,
      refundPayment,
      items,
    };

    setProcessing(true);
    try {
      const result = await billingApi.processSalesReturn(body);
      notify.success(`Return ${result.returnNumber} processed`);
      setReturnOpen(false);
      setDetail(null);
      load();
    } catch (err) {
      showApiError(err, 'Return failed');
    } finally {
      setProcessing(false);
    }
  };

  const handlePrint = async (id: number) => {
    setPrinting(true);
    try {
      await billingApi.openPrintWindow(id);
    } catch (err) {
      showApiError(err, 'Print failed');
    } finally {
      setPrinting(false);
    }
  };

  const columns: Column<InvoiceResponse>[] = [
    { key: 'invoiceNumber', header: 'Invoice #' },
    { key: 'invoiceDate', header: 'Date', render: (r) => formatDateTime(r.invoiceDate) },
    {
      key: 'totalAmount',
      header: 'Total',
      render: (r) => formatCurrency(r.totalAmount, r.currencyCode ?? currency),
    },
    { key: 'paymentStatus', header: 'Payment' },
    { key: 'invoiceStatus', header: 'Status' },
    {
      key: 'actions',
      header: '',
      render: (r) => (
        <div className="flex gap-2">
          <button type="button" className="text-primary-600" onClick={() => openDetail(r.id)}>
            View
          </button>
          <button
            type="button"
            className="text-slate-600"
            onClick={() => handlePrint(r.id)}
          >
            Print
          </button>
        </div>
      ),
    },
  ];

  const hadWalletPayment = detail?.payments?.some((p) => p.paymentMethod === 'WALLET');

  if (!store) {
    return (
      <div>
        <PageHeader title="Invoices" />
        <p className="text-sm text-slate-600">Select a store from the header to view invoice history.</p>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Invoice history"
        description={`${store.name} · ${currency} — list, print, PDF, and sales returns`}
        breadcrumbs={[{ label: 'POS', to: '/pos/dashboard' }, { label: 'Invoices' }]}
      />
      <div className="mb-4 flex flex-wrap items-end gap-4">
        <DateRangeFields from={from} to={to} onFromChange={setFrom} onToChange={setTo} />
        <Button onClick={load} loading={loading}>
          Apply
        </Button>
      </div>
      <DataTable columns={columns}
        data={data}
        keyField="id"
        loading={loading}
        search={pagination.search}
        onSearchChange={(v) => { pagination.setSearch(v); pagination.resetPage(); }}
        page={pagination.page}
        totalPages={pagination.totalPages}
        totalElements={pagination.totalElements}
        onPageChange={pagination.setPage}
      />

      <Modal
        open={!!detail && !returnOpen}
        onClose={() => setDetail(null)}
        title={detail ? `Invoice ${detail.invoiceNumber}` : 'Invoice'}
        size="lg"
        footer={
          detail ? (
            <>
              <Button variant="secondary" onClick={() => handlePrint(detail.id)} loading={printing}>
                Print
              </Button>
              <Button
                variant="secondary"
                onClick={() => billingApi.downloadInvoicePdf(detail.id)}
              >
                PDF
              </Button>
              <Button onClick={() => setReturnOpen(true)}>Refund / Return</Button>
            </>
          ) : null
        }
      >
        {detail && (
          <div className="space-y-3 text-sm">
            <div className="grid gap-2 sm:grid-cols-2">
              <p>
                <span className="text-slate-500">Date:</span>{' '}
                {formatDateTime(detail.invoiceDate)}
              </p>
              <p>
                <span className="text-slate-500">Currency:</span> {detail.currencyCode ?? currency}
              </p>
              <p>
                <span className="text-slate-500">Subtotal:</span>{' '}
                {formatCurrency(detail.subtotal, detail.currencyCode ?? currency)}
              </p>
              <p>
                <span className="text-slate-500">Tax:</span>{' '}
                {formatCurrency(detail.taxAmount ?? 0, detail.currencyCode ?? currency)}
              </p>
              <p>
                <span className="text-slate-500">Total:</span>{' '}
                <strong>{formatCurrency(detail.totalAmount, detail.currencyCode ?? currency)}</strong>
              </p>
              <p>
                <span className="text-slate-500">Paid:</span>{' '}
                {formatCurrency(detail.paidAmount ?? 0, detail.currencyCode ?? currency)} (
                {detail.paymentStatus})
              </p>
            </div>
            {detail.payments && detail.payments.length > 0 && (
              <div>
                <p className="mb-1 font-medium text-slate-700 dark:text-slate-300">Payments</p>
                <ul className="divide-y rounded border border-slate-200 dark:border-slate-700">
                  {detail.payments.map((p) => (
                    <li key={p.id} className="flex justify-between px-3 py-2">
                      <span>{p.paymentMethod}</span>
                      <span>{formatCurrency(p.amount, detail.currencyCode ?? currency)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div>
              <p className="mb-1 font-medium text-slate-700 dark:text-slate-300">Line items</p>
              <ul className="divide-y rounded border border-slate-200 dark:border-slate-700">
                {detail.items?.map((item) => (
                  <li key={item.id} className="flex justify-between px-3 py-2">
                    <span>
                      {item.productName} × {item.quantity}
                      {item.taxPercentage != null && ` (${item.taxPercentage}% tax)`}
                    </span>
                    <span>{formatCurrency(item.lineTotal, detail.currencyCode ?? currency)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={returnOpen && !!detail}
        onClose={() => setReturnOpen(false)}
        title="Sales return"
        footer={
          <>
            <Button variant="secondary" onClick={() => setReturnOpen(false)}>Cancel</Button>
            <Button onClick={processReturn} loading={processing}>Submit return</Button>
          </>
        }
      >
        <p className="mb-3 text-sm text-slate-600">
          Stock will be restored. Refund adjusts payment status
          {hadWalletPayment ? ' and credits the customer wallet for wallet payments.' : '.'}
        </p>
        <Input
          label="Reason (optional)"
          value={returnReason}
          onChange={(e) => setReturnReason(e.target.value)}
          className="mb-4"
        />
        <label className="mb-4 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={refundPayment}
            onChange={(e) => setRefundPayment(e.target.checked)}
          />
          Refund payment (wallet credit if paid via WALLET)
        </label>
        {detail?.items?.map((item) => (
          <div key={item.id} className="mb-3 flex items-center justify-between gap-4 border-b pb-3">
            <div>
              <p className="text-sm font-medium">{item.productName}</p>
              <p className="text-xs text-slate-500">Sold: {item.quantity}</p>
            </div>
            <Input
              label="Return qty"
              type="number"
              min={0}
              max={item.quantity}
              step="0.001"
              className="w-28"
              value={returnQty[item.productId] ?? ''}
              onChange={(e) =>
                setReturnQty((prev) => ({ ...prev, [item.productId]: e.target.value }))
              }
            />
          </div>
        ))}
      </Modal>
    </div>
  );
}
